import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";

const AppointmentContext = createContext(null);

export const AppointmentProvider = ({ children }) => {
  const { token } = useAuth();
  const BASE = process.env.REACT_APP_BASE_URL;

  const [appointmentPaginationData, setAppointmentPaginationData] = useState({
    current_page: 1,
    data: [],
    last_page: 1,
    per_page: 15,
    total: 0,
  });
  const [counts, setCounts] = useState({ total: 0, pending: 0, confirmed: 0, cancelled: 0 });
  const [disabledDates, setDisabledDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  });

  // ─── Appointments ───────────────────────────────────────────────────────────

  const fetchAppointments = async (params = {}) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    const query = new URLSearchParams(params).toString();
    try {
      const res = await fetch(`${BASE}/api/appointments?${query}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch appointments");
      const data = await res.json();
      setAppointmentPaginationData(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BASE}/api/appointments/counts`, { headers: authHeaders() });
      if (!res.ok) throw new Error("Failed to fetch counts");
      const data = await res.json();
      setCounts(data);
    } catch (err) {
      console.error(err);
    }
  };

  const updateAppointment = async (id, payload) => {
    if (!token) return false;
    try {
      const res = await fetch(`${BASE}/api/appointments/${id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to update appointment");
      await fetchAppointments({ page: appointmentPaginationData.current_page });
      await fetchCounts();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const deleteAppointment = async (id) => {
    if (!token) return false;
    try {
      const res = await fetch(`${BASE}/api/appointments/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Failed to delete appointment");
      let newPage = appointmentPaginationData.current_page;
      if (appointmentPaginationData.data.length === 1 && newPage > 1) newPage -= 1;
      await fetchAppointments({ page: newPage });
      await fetchCounts();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  // ─── Disabled Dates ─────────────────────────────────────────────────────────

  const fetchDisabledDates = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BASE}/api/disabled-dates`, { headers: authHeaders() });
      if (!res.ok) throw new Error("Failed to fetch disabled dates");
      const data = await res.json();
      setDisabledDates(data);
    } catch (err) {
      console.error(err);
    }
  };

  const addDisabledDate = async (date, reason = "") => {
    if (!token) return false;
    try {
      const res = await fetch(`${BASE}/api/disabled-dates`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ date, reason }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.errors?.date?.[0] || "Failed to disable date");
      }
      await fetchDisabledDates();
      return true;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const addDisabledDates = async (dates, reason = "") => {
    if (!token) return false;
    if (!Array.isArray(dates) || dates.length === 0) return false;

    // Try batch endpoint first (if backend supports dates array)
    try {
      const res = await fetch(`${BASE}/api/disabled-dates`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ dates, reason }),
      });
      if (res.ok) {
        await fetchDisabledDates();
        return true;
      }
    } catch (e) {
      // Fallback to sequential/individual requests below
    }

    // Fallback: send individually to support APIs accepting single date
    let successCount = 0;
    let lastError = null;
    for (const d of dates) {
      try {
        const res = await fetch(`${BASE}/api/disabled-dates`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ date: d, reason }),
        });
        if (res.ok) {
          successCount++;
        } else {
          const errData = await res.json().catch(() => null);
          if (errData?.errors?.date?.[0]) {
            lastError = errData.errors.date[0];
          }
        }
      } catch (err) {
        lastError = err.message;
      }
    }

    await fetchDisabledDates();
    if (successCount === 0 && lastError) {
      throw new Error(lastError);
    }
    return successCount > 0;
  };

  const removeDisabledDate = async (id) => {
    if (!token) return false;
    try {
      const res = await fetch(`${BASE}/api/disabled-dates/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Failed to remove disabled date");
      await fetchDisabledDates();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  useEffect(() => {
    if (token) {
      fetchAppointments();
      fetchCounts();
      fetchDisabledDates();
    }
  }, [token]);

  return (
    <AppointmentContext.Provider
      value={{
        appointments: appointmentPaginationData.data,
        appointmentPaginationData,
        counts,
        disabledDates,
        loading,
        error,
        fetchAppointments,
        fetchCounts,
        updateAppointment,
        deleteAppointment,
        fetchDisabledDates,
        addDisabledDate,
        addDisabledDates,
        removeDisabledDate,
      }}
    >
      {children}
    </AppointmentContext.Provider>
  );
};

export const useAppointments = () => {
  const ctx = useContext(AppointmentContext);
  if (!ctx) throw new Error("useAppointments must be used within AppointmentProvider");
  return ctx;
};
